import { computed, reactive, readonly, ref } from 'vue'
import { add } from 'date-fns'
import { v4 as uuidv4 } from 'uuid'

import { events$ } from '@/services'

import { DateTimeUtil } from '@/utils/DateTime'
import { logError, logInfo } from '@/helpers/Logger'

import { CUSTOMER_FLOW_SALES_PARTNER_NUMBER, FLOW_TYPE, SESSION_EOL, SESSION_GROUP, STORAGE_KEY } from '@/config/constants'
import { EVENT_SESSION } from '@/config/events'
import { ReactivityUtil } from '@/utils/Reactivity'
import { CookieUtil } from '@/utils/Cookie'

// DATA
const consoleColor = 'color: green'
let sessionKey = STORAGE_KEY
const store = reactive({
  active: false,
  randomSessionId: null,
  leadCode: null,
  previousRoute: null,
  isPartnerFlow: false,
  salesPartnerNumber: null,
  leadgiver: null,
  flowType: null,
  applicationStarted: false,
  isReentryFlow: false,
})
// Not alphabetically as the store needs to be initialized first
const session = readonly(store)
const initialized = ref(false)

function sessionStore() {
  // Initialize session from storage on startup
  function initializeFromStorage() {
    const storedSession = readFromStorage()
    if (storedSession && storedSession.active) {
      // Ensure applicationStarted defaults to false if not present
      const sessionWithDefaults = {
        ...storedSession,
        applicationStarted: storedSession.applicationStarted ?? false
      }
      ReactivityUtil.reAssign(store, sessionWithDefaults)
      logInfo(['%cSESSION RESTORED', consoleColor, 'From storage', 'RandomSessionId:', storedSession.randomSessionId])
    }
  }

  function readFromStorage(key = sessionKey) {
    const item = CookieUtil.read(key)

    let value
    try {
      value = item && JSON.parse(item)
    } catch (error) {
      logError(['Failed to get session from localstorage\n', error, item])
    }

    const hasExpired = (value?.eol && !DateTimeUtil.isInFuture(new Date(value?.eol)))

    if (!value || hasExpired) {
      return false
    } else {
      return { ...value }
    }
  }

  function removeFromStorage(key) {
    CookieUtil.delete(key)
  }

  function writeToStorage(key, payload) {
    CookieUtil.set({
      key,
      value: JSON.stringify(payload),
    })
  }

  function removeAllCookies() {
    CookieUtil.deleteAppGroupCookies([SESSION_GROUP.LEAD], sessionKey)
  }

  // METHOD
  function clearSession(opts = { silent: false }) {
    if (!store.active) return Promise.resolve()

    const randomSessionId = store.randomSessionId
    const leadCode = store.leadCode

    if (!opts.silent) {
      ReactivityUtil.reAssign(store, { 
        active: false,
        randomSessionId: null,
        leadCode: null,
        previousRoute: null,
        isPartnerFlow: false,
        salesPartnerNumber: null,
        leadgiver: null,
        applicationStarted: false
      })
    }

    if (leadCode) {
      logInfo(['%cCLEARED SESSION', consoleColor, leadCode, 'RandomSessionId:', randomSessionId])
    }

    removeFromStorage(sessionKey)
    return Promise.resolve()
  }

  /**
   * save "session" into localStorage item
   *
   * @param <Object>payload - leadObject
   * @params <Object>options - hot; setOwner; preventCookie;
   *
   */
  function setSession(payload, options = { hot: true }) {
    // Guard against undefined/null payload
    if (!payload) {
      logError(['setSession called with undefined/null payload'])
      return Promise.reject(new Error('Invalid payload provided to setSession'))
    }

    const { leadCode } = payload
    const currentSession = { leadCode }

    let localSession
    if (!options.preventCookie) {
      localSession = readFromStorage(sessionKey)
    }

    if (localSession?.hot) {
      currentSession.eol = add(new Date(), SESSION_EOL)
      currentSession.hot = true
    } else {
      if ((!payload.email || !payload.mobile) && options.hot) {
        currentSession.eol = add(new Date(), SESSION_EOL)
      }
      currentSession.hot = options.hot
    }

    currentSession.active = true
    currentSession.previousRoute = store.previousRoute

    // Preserve randomSessionId across session updates
    if (store.randomSessionId) {
      currentSession.randomSessionId = store.randomSessionId
    }

    // Only include partner properties when they have meaningful values
    if (store.isPartnerFlow === true) {
      currentSession.isPartnerFlow = true
    }
    if (store.salesPartnerNumber && store.salesPartnerNumber !== null) {
      currentSession.salesPartnerNumber = store.salesPartnerNumber
    }
    if (store.leadgiver && store.leadgiver !== null) {
      currentSession.leadgiver = store.leadgiver
    }
    if (store.flowType && store.flowType !== null) {
      currentSession.flowType = store.flowType
    }

    currentSession.applicationStarted = store.applicationStarted
    ReactivityUtil.reAssign(store, currentSession)

    // @note: For some cases, we can't write a cookie (e.g. Entry)
    if (!options.preventCookie) {
      writeToStorage(sessionKey, currentSession)
    }

    if (leadCode) {
      logInfo(['%cSESSION UPDATED', consoleColor, leadCode])
      events$.emit(EVENT_SESSION.UPDATE, { randomSessionId: store.randomSessionId })
    }
  
    return Promise.resolve(currentSession)
  }

  function getSession() {
    const cookieData = readFromStorage()

    if (cookieData) {
      return cookieData
    } else if (!session.active) {
      return false
    } else {
      return store
    }
  }

  function activateSession(useSpecificRandomSessionId = null) {
    // Check if there's existing applicationStarted value in storage to preserve
    const existingSessionData = readFromStorage() || {}
    const preservedApplicationStarted = existingSessionData.applicationStarted || store.applicationStarted || false
    
    // Use provided randomSessionId or generate new one
    const randomSessionId = useSpecificRandomSessionId || uuidv4()

    // Directly set store properties to avoid timing issues
    store.active = true
    store.leadCode = null
    store.randomSessionId = randomSessionId
    store.applicationStarted = preservedApplicationStarted

    // Persist the session state
    const sessionData = {
      active: true,
      randomSessionId: randomSessionId,
      leadCode: null,
      eol: add(new Date(), SESSION_EOL),
      hot: true,
      applicationStarted: preservedApplicationStarted
    }
    
    // Only include partner properties when they have meaningful values
    if (store.isPartnerFlow === true) {
      sessionData.isPartnerFlow = true
    }

    if (store.salesPartnerNumber && store.salesPartnerNumber !== null) {
      sessionData.salesPartnerNumber = store.salesPartnerNumber
    }

    if (store.leadgiver && store.leadgiver !== null) {
      sessionData.leadgiver = store.leadgiver
    }

    if (store.flowType && store.flowType !== null) {
      sessionData.flowType = store.flowType
    }
    
    
    writeToStorage(sessionKey, sessionData)
    logInfo(['%cSESSION ACTIVATED', consoleColor, 'Insurance started', 'RandomSessionId:', randomSessionId])
  }

  function setSessionKey(newSessionKey) {
    sessionKey = newSessionKey
  }

  function setCurrentRoute(routeName) {
    store.previousRoute = routeName
    
    // Persist current route in storage if session is active
    if (store.active) {
      const sessionData = getSession() || {}
      sessionData.previousRoute = routeName
      writeToStorage(sessionKey, sessionData)
    }
  }

  function getCurrentRoute() {
    return store.previousRoute
  }

  /**
   * Get the current random session ID for analytics tracking
   * @returns {string|null} - The random session ID or null if not active
   */
  function getRandomSessionId() {
    return store.randomSessionId
  }

  /**
   * Reset leadCode to null in the session store and storage
   */
  function resetLeadCode() {
    store.leadCode = null

    if (store.active) {
      const sessionData = readFromStorage(sessionKey) || {}
      sessionData.leadCode = null
      writeToStorage(sessionKey, sessionData)
    }

    logInfo(['%cLEAD CODE RESET', consoleColor, 'leadCode set to null'])
  }

  /**
   * Generate and persist a new random session ID without touching the rest of the session
   * @returns {string} - The newly generated random session ID
   */
  function resetRandomSessionId() {
    const randomSessionId = uuidv4()
    store.randomSessionId = randomSessionId

    if (store.active) {
      const sessionData = readFromStorage(sessionKey) || {}
      sessionData.randomSessionId = randomSessionId
      writeToStorage(sessionKey, sessionData)
    }

    logInfo(['%cRANDOM SESSION ID RESET', consoleColor, 'New RandomSessionId:', randomSessionId])
    return randomSessionId
  }

  /**
   * Set partner flow flag
   * @param {boolean} isPartner - Whether this is a partner flow
   */
  function setPartnerFlow(isPartner) {
    store.isPartnerFlow = isPartner
    
    // Persist to session storage
    if (store.active) {
      const sessionData = getSession() || {}
      
      if (isPartner) {
        sessionData.isPartnerFlow = isPartner
      }

      writeToStorage(sessionKey, sessionData)
    }
  }

  /**
   * Check if this is a partner flow
   * @returns {boolean} - Whether this is a partner flow
   */
  function isPartnerFlow() {
    return store.isPartnerFlow
  }

  /**
   * Set sales partner number
   * @param {string|null} salesPartnerNumber - Sales partner number to set
   */
  function setSalesPartnerNumber(salesPartnerNumber) {
    store.salesPartnerNumber = salesPartnerNumber

    if (store.active) {
      const sessionData = readFromStorage(sessionKey) || {}
      sessionData.salesPartnerNumber = salesPartnerNumber
      writeToStorage(sessionKey, sessionData)
    }    
  }

  /**
   * Get sales partner number
   * @returns {string|null} - Sales partner number or null
   */
  function getSalesPartnerNumber() {
    return store.salesPartnerNumber
  }

  /**
   * Get the sales partner number that has to be sent within the lead payload.
   * In a customer flow the tracked `salesPartnerNumber` always stays the default customer flow one,
   * but the payload has to contain the sales partner number provided within the url (`leadgiver`)
   * whenever it differs from the default one.
   * @returns {string|null} - Sales partner number to be used in the lead payload
   */
  function getPayloadSalesPartnerNumber() {
    const isCustomerFlow = store.flowType === FLOW_TYPE.CUSTOMER_FLOW
    const leadgiver = store.leadgiver

    if (isCustomerFlow && leadgiver && leadgiver !== CUSTOMER_FLOW_SALES_PARTNER_NUMBER) {
      return leadgiver
    }

    return store.salesPartnerNumber
  }

  /**
   * Set the leadgiver (sales partner number provided in a customer flow entry url)
   * @param {string|null} leadgiver - Leadgiver number to set
   */
  function setLeadgiver(leadgiver) {
    store.leadgiver = leadgiver

    if (store.active) {
      const sessionData = readFromStorage(sessionKey) || {}
      sessionData.leadgiver = leadgiver
      writeToStorage(sessionKey, sessionData)
    }
  }

  /**
   * Get the leadgiver
   * @returns {string|null} - Leadgiver number or null
   */
  function getLeadgiver() {
    return store.leadgiver
  }

  /**
   * Set reentry flow flag
   * @param {boolean} isReentry - Whether this is a reentry flow
   */
  function setReentryFlow(isReentry) {
    store.isReentryFlow = isReentry
  }

  /**
   * Check if this is a reentry flow
   * @returns {boolean} - Whether this is a reentry flow
   */
  function isReentryFlow() {
    return store.isReentryFlow
  }

  /**
   * Set the flow type (e.g. CUSTOMER_FLOW, EMPLOYEE_FLOW)
   * @param {string|null} flowType - The flow type to set
   */
  function setFlowType(flowType) {
    store.flowType = flowType
    const sessionData = readFromStorage(sessionKey) || {}
    
    if (flowType) {
      sessionData.flowType = flowType
    }
    
    writeToStorage(sessionKey, sessionData)
  }
  
  /**
   * Get the current flow type
   * @returns {string|null} - The current flow type or null
   */
  function getFlowType() {
    return store.flowType
  }

  /**
   * Set application started flag - indicates user clicked "Let's go"
   * @param {boolean} started - Whether application has been started
   */
  function setApplicationStarted(started) {
    store.applicationStarted = started
    
    // Always persist applicationStarted to session storage (even if session inactive)
    const sessionData = getSession() || {}
    
    // Always store the applicationStarted value (true or false)
    sessionData.applicationStarted = started
    
    writeToStorage(sessionKey, sessionData)
    
    if (started) {
      logInfo(['%cAPPLICATION STARTED', consoleColor, 'User clicked Let\'s go', 'RandomSessionId:', store.randomSessionId])
    }
  }

  /**
   * Check if application has been started (user clicked "Let's go")
   * @returns {boolean} - Whether application has been started
   */
  function isApplicationStarted() {
    return store.applicationStarted
  }

  /**
   * Wait for the sessionStore to be initialized and ready
   * @returns {Promise} - Resolves when store is ready
   */
  function ready() {
    return new Promise(function (resolve) {
      if (initialized.value) {
        resolve()
        return
      }

      // Don't automatically restore session - only initialize as ready
      Promise.resolve().then(() => {
        initialized.value = true
        resolve()
      })
    })
  }

  return {
    activateSession,
    clearSession,
    getCurrentRoute,
    getFlowType,
    getLeadgiver,
    getPayloadSalesPartnerNumber,
    getSalesPartnerNumber,
    getSession,
    getRandomSessionId,
    initializeFromStorage,
    isActive: computed(() => session.active),
    isApplicationStarted,
    isPartnerFlow,
    isReentryFlow,
    ready,
    removeAllCookies,
    resetLeadCode,
    resetRandomSessionId,
    session,
    setApplicationStarted,
    setCurrentRoute,
    setFlowType,
    setLeadgiver,
    setPartnerFlow,
    setReentryFlow,
    setSalesPartnerNumber,
    setSession,
    setSessionKey,
  }
}

export default sessionStore()
